/*    
<summary>
   This class component is all about Managing Option data functionality.
   Developer:Aashish Singh, Created Date:29-August-2023
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/
import { action, computed, makeObservable, observable, toJS } from "mobx";
import moment from "moment";
import toast from "react-hot-toast";
import { emptyValue } from "../../constants/constants";
import {
  registrationTypeOptions
} from "../../constants/options.constant";
import URLConstants from "../../constants/url-constants";
import config from "../../helpers/config-helper";
import { getBusinessTypeIdFromLocalStorage, getLanguageFromLocalStorage, getStoreIdFromLocalStorage, getUserTypeFromLocalStorage } from "../../helpers/localstorage-helper";
import { IObservableInitialState, IOption } from "../../models/ICommon";
import {
  IOptionInitialFilterState,
  IOptionStoreFilter,
} from "../../models/IFilter";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import { ICommonState } from "../../models/state/ICommonState";
import { formatMessage } from "../../translations/format-message";
import { initialState } from "../initialState/add-edit-option-menu-state";
import { initialState as GetAllOptionMenuState } from "../initialState/get all-option-menu-state";
import * as baseService from "../service/base-service";
import { AuthStore } from "./auth-store";
import { IOptionMenuList, IOptionMenuListVM, IOptionMenuVM } from "../../models/response/IOptionMenuResponse";
import { IAddOptionMenu } from "../../models/forms/IAddOptionMenu";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { optionStoreInitialFilterState } from "../initialState/initial-filter-state";
 
const appConfig = config();
 
const dateFormat = appConfig.REACT_APP_DATE_FORMAT;
const authStore = new AuthStore();
 
export class OptionMenuStore implements ICommonState {
  inProgress = false;
  error = "";
  optionmenuList: IOptionMenuListVM = GetAllOptionMenuState;
 
  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };
 
  addUpdateOptionMenuState = { ...this.initialStateValue };
  deleteOptionMenuState = { ...this.initialStateValue };
 
  activeInactiveState = { ...this.initialStateValue };
  updateStoreOptionOrderState = { ...this.initialStateValue };
 
  optionmenu: IOptionMenuVM | undefined = undefined;
  optionmenuState = { ...this.initialStateValue };
  optionFilterStore: IOptionStoreFilter = { ...optionStoreInitialFilterState };
 
  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      optionmenuList: observable,
      optionmenu: observable,
      addUpdateOptionMenuState: observable,
      deleteOptionMenuState: observable,
      optionmenuState: observable,
      updateStoreOptionOrderState: observable,
      optionFilterStore: observable,
      //   optionmenuFilter: observable,
      GetOptionMenuListService: action,
      GetSearchOptionRegistrationFilterService: action,
      AddOptionMenuService: action,
      UpdateOptionMenuService: action,
      UpdateOptionMenuStatusService: action,
      DeleteOptionMenuService: action,
      GetOptionMenuByIdService: action,
      reset: action,
      resetOption: action,
      resetOptionMenuDetail: action,
      resetAddUpdateOptionMenuState: action,
      UpdateStoreOptionOrderIdService: action,
      setOptionStoreFilterDetail: action,
      resetOptionStoreFilterDetail: action,
      allOptionMenu: computed,
      optionmenuDetail: computed,
      getOptionStoreFilter: computed,
    });
  }
 
  /**
   * This function is used to Get All StaffManagements List data with pagination information by calling an API.
   * @param currentPage : Current Page Number
   * @param pagerSize : Page Size
   * @returns
   */
  GetOptionMenuListService = (
    currentPage: number,
    pagerSize: number,
    orderBy: string,
    column: string
  ) => {
    this.inProgress = true;
    let storeId = getStoreIdFromLocalStorage() && getStoreIdFromLocalStorage() > 0 ? getStoreIdFromLocalStorage() : null;
    let businessTypeId = getBusinessTypeIdFromLocalStorage() && getBusinessTypeIdFromLocalStorage() > 0 ? getBusinessTypeIdFromLocalStorage() : null;
    let url = `${URLConstants.GetOptionMenuListStore}?PageNo=${currentPage}&PageSize=${pagerSize}&OrderBy=${orderBy}&Column=${column}&storeId=${storeId}&businessTypeID=${businessTypeId}`;
    if (getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.HeadQuarter])
      url = url + "&BusinessTypeId=" + getBusinessTypeIdFromLocalStorage();
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IOptionMenuListVM>>) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.optionmenuList = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };
 
 
 
 
  /**
   * This function is used to fetch the list of optionmenu registrations based on the provided search query and pagination details.
   * @param currentPage : Current Page Number (used for pagination)
   * @param pagerSize : Number of items per page (used for pagination)
   * @param searchQuery : Search query string that filters optionmenu registrations
   * @returns
   */
 
 
  GetSearchOptionRegistrationFilterService = (
    currentPage: number,
    pagerSize: number,
    searchQuery: string,
    isInvalid: boolean, // Now handling the filter logic for `is_invalid` here
    column: string,
    orderBy: string
  ) => {
    this.inProgress = true;
    // Adding filter logic (e.g., `is_invalid`) to the search query URL
    let url = `/OptionManagement/Search/Filter?PageNo=${currentPage}&PageSize=${pagerSize}&Search=${encodeURIComponent(searchQuery)}&is_invalid=${isInvalid}&column=${column}&orderBy=${orderBy}`;
 
    if (getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.HeadQuarter])
      url = url + "&BusinessTypeId=" + getBusinessTypeIdFromLocalStorage();
    else if (getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store])
      url = url + "&BusinessTypeId=" + getBusinessTypeIdFromLocalStorage() + "&storeId=" + getStoreIdFromLocalStorage();
 
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IOptionMenuListVM>>) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.optionmenuList = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };
 
 
 
  /**
   * This function is used to map staffList to allStaffManagement List suitable for Grid Component.
   * @returns allStaffManagementsList
   */
  get allOptionMenu(): IOptionMenuList[] {
    if (this.optionmenuList?.OptionVM && this.optionmenuList.OptionVM.length > 0) {
      const options = this.optionmenuList.OptionVM.map((optionmenu) => {
        let registrationArray: IOption[] = registrationTypeOptions.filter(
          (option: IOption) => option.id === optionmenu.RegistrationType
        );
 
        return {
          Id: optionmenu.Id,
          RegistrationType:
            registrationArray.length > 0
              ? registrationArray[0].id > 0 ? optionmenu.StoreName ?? emptyValue : optionmenu.HeadquarterName ?? emptyValue
              : emptyValue,
          BusinessTypeId: optionmenu.BusinessTypeId,
          Treatment: optionmenu.Treatment,
          ReservationSite: optionmenu.ReservationSite,
          OptionName: optionmenu.OptionName,
          TimeId: optionmenu.TimeId,
          Time: optionmenu.Time,
          TimeValue: optionmenu.TimeValue,
          FeeExcludingTax: optionmenu.FeeExcludingTax,
          TaxId: optionmenu.TaxId,
          TaxAmount: optionmenu.TaxAmount,
          TaxValue: optionmenu.TaxValue,
          FeeIncludingTax: optionmenu.FeeIncludingTax,
          Description: optionmenu.Description,
          StartDate: optionmenu.StartDate,
          EndDate: optionmenu.EndDate,
          Status: optionmenu.Status,
          Remarks: optionmenu.Remarks,
          isDelete: true,
          isEdit: true,
          OrderId: optionmenu.OptionDetailsVM?.OrderId,
          OptionDetailId: optionmenu.OptionDetailsVM?.Id
        };
      });
 
      // let userType = getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store]
 
      // if (userType) {
      //   return options.sort((a, b) => {
      //     // Handle cases where OrderId might be undefined
      //     const orderIdA = a.OrderId || 0;
      //     const orderIdB = b.OrderId || 0;
      //     return orderIdA - orderIdB;
      //   });
      // }
 
      return options;
    }
    return [];
  }
 
 
 
 
  /**
   * This function is used to Get StaffManagement Details by Id by calling an API.
   * @param id : The StaffManagement Identifier
   * @returns
   */
  GetOptionMenuByIdService = (id: number) => {
    this.optionmenuState.inProgress = true;
    let userType = getUserTypeFromLocalStorage();
    let storeId = userType === userTypeEnum[userTypeEnum.Store] ? getStoreIdFromLocalStorage() : null;
    const urlParams = new URLSearchParams();
    if(storeId)
        urlParams.append("storeId", String(storeId));
    const url =
      `${URLConstants.GetOptionById}/${id}` +
      "?" +
      urlParams.toString();
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IOptionMenuVM>>
        ) => {
          if (response.data.Error) {
            this.optionmenuState.error = response.data.Message;
          } else {
            let data = response.data.Data;
            this.optionmenu = data;
          }
        }
      )
      .catch((err: string) => {
        this.optionmenuState.error = err;
      })
      .finally(
        action(() => {
          this.optionmenuState.inProgress = false;
        })
      );
  };
 
  /**
   * This function provides initail values to the Add StaffManagement Form.
   * @returns StaffManagement Details
   *
   */
  get optionmenuDetail(): any {
    if (this.optionmenu) {
      return {
        RegistrationType: this.optionmenu.RegistrationType || 0,
        BusinessTypeId: this.optionmenu.BusinessTypeId ?? -1,
        Treatment: Array.isArray(this.optionmenu.Treatment)
          ? this.optionmenu.Treatment.map((treatment) => treatment.TreatmentId)
          : [],
        ReservationSite:
          this.optionmenu.ReservationSite !== undefined &&
            this.optionmenu.ReservationSite !== null
            ? this.optionmenu.ReservationSite ? "1" : "0"
            : "0",
        OptionName: this.optionmenu.OptionName ?? "",
        TimeId: this.optionmenu.TimeId ?? 0,
        Time: this.optionmenu.Time,
        TimeValue: this.optionmenu.TimeValue ?? 0,
        FeeExcludingTax: this.optionmenu.FeeExcludingTax ?? 0,
        TaxId: this.optionmenu.TaxId ?? 0,
        TaxValue: this.optionmenu.TaxValue ?? 0,
        TaxAmount: this.optionmenu.TaxAmount ?? 0,
        FeeIncludingTax: this.optionmenu.FeeIncludingTax ?? 0,
        Description: this.optionmenu.Description ?? "",
        StartDate:
          this.optionmenu.StartDate == "0001-01-01T00:00:00.0000000Z"
            ? ""
            : (this.optionmenu.StartDate ? moment(this.optionmenu.StartDate).format(dateFormat) : ""),
        EndDate:
          this.optionmenu.EndDate == "0001-01-01T00:00:00.0000000Z"
            ? ""
            : (this.optionmenu.EndDate ? moment(this.optionmenu.EndDate).format(dateFormat) : ""),
        Status:
          this.optionmenu.Status !== undefined && this.optionmenu.Status !== null
            ? String(this.optionmenu.Status)
            : "0",
        Remarks: this.optionmenu.Remarks ?? "",
        StoreName: this.optionmenu.StoreName ?? "",
        HeadquarterName: this.optionmenu.HeadquarterName ?? "",
        RegistrationTypeName: this.optionmenu.RegistrationType > 0 ? this.optionmenu.StoreName ?? "" : this.optionmenu.HeadquarterName ?? "",
        StoreId: this.optionmenu.StoreId ?? null,
        IsAutoCalculateTax: false
      };
    }
    return {
      ...initialState, BusinessTypeId: getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.HeadQuarter] || getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store]
        ? getBusinessTypeIdFromLocalStorage() : -1,
      RegistrationType: getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.HeadQuarter] ? 0 : getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store] ? 1 : initialState.RegistrationType,
    };
  }
 
 
 
  /*
    This function is used to reset all staff observables to their initial values.  
    */
  resetOptionMenuDetail = () => {
    this.optionmenu = undefined;
    this.optionmenuState = { ...this.initialStateValue };
  };
 
  /**
   * This function is used to Add New StaffManagement by calling an API.
   * @param data : StaffManagement Data
   * @returns
   */
  AddOptionMenuService = (data: IAddOptionMenu) => {
    this.addUpdateOptionMenuState.inProgress = true;
    let url = URLConstants.AddOptionMenu;
    let storeId = getStoreIdFromLocalStorage() ? getStoreIdFromLocalStorage() : null;
    let userType = getUserTypeFromLocalStorage();
    const requestData = {
      ...data,
      ReservationSite: data.ReservationSite?.toString() === "1" ? true : false,
      RegistrationType: data.RegistrationType?.toString(),
      StoreId: userType === userTypeEnum[userTypeEnum.HeadQuarter] ? null : storeId
    };
 
    return baseService
      .postRequest(url, requestData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateOptionMenuState.error = response.data.Message;
        } else {
          this.addUpdateOptionMenuState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateOptionMenuState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateOptionMenuState.inProgress = false;
        })
      );
  };
 
  /**
   * This function is used to Updating Existing StaffManagement Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param data
   * @returns
   */
  UpdateOptionMenuService = (
    id: number,
    data: IAddOptionMenu
  ) => {
    this.addUpdateOptionMenuState.inProgress = true;
    let url = `${URLConstants.UpdateOptionMenu}`;
    let userType = getUserTypeFromLocalStorage();
    let storeId = userType === userTypeEnum[userTypeEnum.Store] ? getStoreIdFromLocalStorage() : null;
    let storeIdUpdated = data.StoreId && data.StoreId > 0 ? data.StoreId : storeId;
    const requestData = {
      Id: id,
      ...data,
      ReservationSite: data.ReservationSite?.toString() === "1" ? true : false,
      RegistrationType: data.RegistrationType?.toString(),
      StoreId: storeIdUpdated
    };
 
    return baseService
      .putRequest(url, requestData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateOptionMenuState.error = response.data.Message;
        } else {
          this.addUpdateOptionMenuState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateOptionMenuState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateOptionMenuState.inProgress = false;
        })
      );
  };
 
  UpdateStoreOptionOrderIdService = (data: any) => {
    this.updateStoreOptionOrderState.inProgress = true;
    let url = URLConstants.UpdateStoreOptionOrderId;
    let storeId = getStoreIdFromLocalStorage() ? getStoreIdFromLocalStorage() : null;
    let swapOrder = data.map((item: any) => item?.OrderId);
    data[0].OrderId = swapOrder[1];
    data[1].OrderId = swapOrder[0];
    let mappedData = data.map((item: any) => {
      return {
        Id: item.OptionDetailId,
        OrderId: item.OrderId ?? -1,
        StoreId: storeId,
      };
    })
    return baseService
      .putRequest(url, { optionDetails: mappedData })
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.updateStoreOptionOrderState.error = response.data.Message;
        } else { this.updateStoreOptionOrderState.success = true };
      })
      .catch((err: string) => {
        this.updateStoreOptionOrderState.error = err;
      })
      .finally(
        action(() => {
          this.updateStoreOptionOrderState.inProgress = false;
        })
      );
  };
 
 
  resetUpdateStoreOptionOrderState = () => {
    this.updateStoreOptionOrderState.inProgress = false;
    this.updateStoreOptionOrderState.success = false;
    this.updateStoreOptionOrderState.error = "";
  };
  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetAddUpdateOptionMenuState = () => {
    this.addUpdateOptionMenuState.inProgress = false;
    this.addUpdateOptionMenuState.success = false;
    this.addUpdateOptionMenuState.error = "";
  };
 
  /**
   * This function is used to Delete StaffManagement by calling an API.
   * @param id : The StaffManagement Identifier
   * @returns
   */
  DeleteOptionMenuService = (id: number) => {
    this.deleteOptionMenuState.inProgress = true;
    const urlParams = new URLSearchParams();
    const userType = getUserTypeFromLocalStorage();
    const businesTypeId = getBusinessTypeIdFromLocalStorage();
    const storeId = getStoreIdFromLocalStorage();
    if(userType === userTypeEnum[userTypeEnum.HeadQuarter]){
      if(businesTypeId > 0)
        urlParams.append("BusinessTypeId", String(businesTypeId));
    }
    else{
      if(storeId > 0 && businesTypeId > 0){
        urlParams.append("StoreId", String(storeId));
        urlParams.append("BusinessTypeId", String(businesTypeId));
      }
    }
    const url =
      `${URLConstants.DeleteOptionMenu}/${id}` +
      "?" +
      urlParams.toString();
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteOptionMenuState.error = response.data.Message;
        } else {
          this.deleteOptionMenuState.success = true;
        }
      })
      .catch((err: string) => {
        this.deleteOptionMenuState.error = err;
      })
      .finally(
        action(() => {
          this.deleteOptionMenuState.inProgress = false;
        })
      );
  };
 
  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetDeleteOptionMenu = () => {
    this.deleteOptionMenuState.inProgress = false;
    this.deleteOptionMenuState.success = false;
    this.deleteOptionMenuState.error = "";
  };
 
  /**
   * This function is used to Updating Existing StaffManagement state (i.e. active/inactive) Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param state: StaffManagement state
   * @returns
   */
  UpdateOptionMenuStatusService = (id: number, state: boolean) => {
    this.activeInactiveState.inProgress = true;
    let url = URLConstants.OptionMenuStatus;
    let optionmenuData: any = {
      Id: id,
      IsActive: state,
    };
    return baseService
      .putRequest(url, optionmenuData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.activeInactiveState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.activeInactiveState.success = true;
      })
      .catch((err: string) => {
        this.activeInactiveState.error = err;
      })
      .finally(
        action(() => {
          this.activeInactiveState.inProgress = false;
        })
      );
  };
 
  /*
        This function is used to set the filter details to the Filter observable.  
        */
  setOptionStoreFilterDetail = (filter: IOptionStoreFilter) => {
    this.optionFilterStore = { ...filter };
  };
 
  resetOptionStoreFilterDetail = () => {
    this.optionFilterStore = { ...optionStoreInitialFilterState };
  };
 
  get getOptionStoreFilter(): IOptionStoreFilter {
    return this.optionFilterStore;
  }
 
  /*
    This function is used to reset all observables to their initial values.  
    */
  reset = () => {
    this.error = "";
    this.inProgress = false;
    this.addUpdateOptionMenuState.success = false;
    this.addUpdateOptionMenuState.error = "";
    this.deleteOptionMenuState = { ...this.initialStateValue };
    this.addUpdateOptionMenuState.inProgress = false;
    this.optionmenuState = { ...this.initialStateValue };
  };
 
  /**
   * This function is used to reset all staff observables to their initial values.
   * @returns
   */
  resetOption = () => {
    this.error = "";
    this.inProgress = false;
    this.optionmenuList = GetAllOptionMenuState;
 
    this.addUpdateOptionMenuState = { ...this.initialStateValue };
    this.deleteOptionMenuState = { ...this.initialStateValue };
    this.optionmenu = undefined;
    this.optionmenuState = { ...this.initialStateValue };
  };
}
 
export default new OptionMenuStore();